import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, CreditCard, Check, Shield, Sparkles, Crown, Star, X, Loader2, Lock, DollarSign, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const planId = searchParams.get('plan') || searchParams.get('id');
  const typeParam = searchParams.get('type');
  const challengeId = searchParams.get('challenge');
  const ebookId = searchParams.get('ebook');
  const videoId = searchParams.get('video');
  const contentId = searchParams.get('content');
  const discountFromUrl = searchParams.get('discount');
  
  const getProductType = () => {
    if (typeParam === 'plan' || planId) return 'plan';
    if (typeParam === 'challenge' || challengeId) return 'challenge';
    if (typeParam === 'ebook' || ebookId) return 'ebook';
    if (typeParam === 'video' || videoId) return 'video';
    if (typeParam === 'content' || contentId) return 'content';
    return 'plan';
  };
  
  const getProductId = () => {
    const type = getProductType();
    if (type === 'plan') return planId || 'premium';
    if (type === 'challenge') return challengeId || searchParams.get('id');
    if (type === 'ebook') return ebookId || searchParams.get('id');
    if (type === 'video') return videoId || searchParams.get('id');
    if (type === 'content') return contentId || searchParams.get('id');
    return planId || searchParams.get('id') || 'premium';
  };
  
  const productType = getProductType();
  const productId = getProductId();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState(discountFromUrl || '');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [productInfo, setProductInfo] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(4200);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const [guestPasswordConfirm, setGuestPasswordConfirm] = useState('');
  const [guestWhatsapp, setGuestWhatsapp] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  // Periodos de precios (Mensual/Anual/etc) leídos del backend
  const [pricingPeriods, setPricingPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  
  // Los precios se cargan dinámicamente desde el backend
  const [PLANS, setPLANS] = useState({});

  useEffect(() => {
    checkAuth();
    loadExchangeRate();
    loadPlansFromBackend();
  }, []);

  // Cargar periodos de precios activos cuando se conoce el productId
  useEffect(() => {
    const loadPricingPeriods = async () => {
      if (!productId) return;
      try {
        const realProgramId = PLANS[productId]?.program_id || productId;
        const res = await axios.get(`${BACKEND_URL}/api/subscription-pricing`, {
          params: { program_id: realProgramId }
        });
        const rows = res.data || [];
        setPricingPeriods(rows);
        if (rows.length > 0) {
          setSelectedPeriodId(rows[0].pricing_id || rows[0]._id);
        } else {
          setSelectedPeriodId(null);
        }
      } catch (_) {
        setPricingPeriods([]);
      }
    };
    loadPricingPeriods();
  }, [productId, PLANS]);

  const selectedPeriod = pricingPeriods.find(
    (p) => (p.pricing_id || p._id) === selectedPeriodId
  );

  // Cargar producto cuando los planes estén listos
  useEffect(() => {
    if (Object.keys(PLANS).length > 0 || productType !== 'plan') {
      loadProductInfo();
    }
  }, [productId, productType, PLANS]);

  // Cargar precios de planes desde el backend
  const loadPlansFromBackend = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/programs`);
      const programs = response.data || [];
      
      const plansMap = {};
      programs.forEach(program => {
        // Usar program_id como key PRINCIPAL (único). Solo hacemos alias por level
        // cuando no hay colisión, para no romper enlaces legacy tipo /checkout?plan=premium
        const uniqueKey = program.program_id || program.id;
        const levelKey = program.level;
        const planData = {
          name: program.name || program.title,
          description: program.description,
          price: program.price || 0,
          original_price: program.original_price || null, // Precio gancho
          period: program.payment_type === 'annual' ? 'pago anual' : 'pago único',
          icon: levelKey === 'premium' ? Crown : (levelKey === 'nivel_2' ? Star : Sparkles),
          color: levelKey === 'premium' ? 'amber' : (levelKey === 'nivel_2' ? 'purple' : 'pink'),
          features: program.features || [],
          program_id: program.program_id
        };
        
        // Mapear siempre por program_id único (fuente de verdad)
        if (uniqueKey) plansMap[uniqueKey] = planData;
        
        // Alias por level - solo si nadie más lo ocupó (primer programa con ese level manda,
        // los siguientes deben usar su program_id único en la URL).
        if (levelKey && !plansMap[levelKey]) {
          plansMap[levelKey] = planData;
        }
        
        // Alias hacia atrás para nombres legacy
        if (levelKey === 'nivel_1' && !plansMap['principiante']) plansMap['principiante'] = planData;
        if (levelKey === 'nivel_2' && !plansMap['intermedio']) plansMap['intermedio'] = planData;
        if (levelKey === 'nivel_3' && !plansMap['premium']) plansMap['premium'] = planData;
      });
      
      setPLANS(plansMap);
      console.log('[Checkout] Planes cargados desde backend:', plansMap);
    } catch (error) {
      console.error('[Checkout] Error cargando planes:', error);
      // Fallback a valores por defecto si falla
      setPLANS({
        nivel_1: { name: 'Nivel 1', price: 39, period: 'pago único', icon: Sparkles, color: 'pink', features: [] },
        nivel_2: { name: 'Nivel 2', price: 59, period: 'pago único', icon: Star, color: 'purple', features: [] },
        premium: { name: 'Club VIP', price: 99, period: 'pago anual', icon: Crown, color: 'amber', features: [] }
      });
    }
  };

  useEffect(() => {
    // Aplicar descuento automáticamente si viene de la URL y el producto ya tiene precio
    if (discountFromUrl && productInfo?.price > 0 && !appliedDiscount && !validatingDiscount) {
      console.log('[Checkout] Aplicando descuento desde URL:', discountFromUrl, 'Precio producto:', productInfo?.price);
      applyDiscountFromUrl(discountFromUrl);
    }
  }, [discountFromUrl, productInfo?.price]);

  const applyDiscountFromUrl = async (code) => {
    if (!code || !code.trim()) return;
    
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-discount`, {
        code: code.trim().toUpperCase(),
        product_id: productInfo?.id || planId || challengeId,
        original_price: productInfo?.price || 0
      });

      if (response.data.valid) {
        setAppliedDiscount({
          code: response.data.code,
          percentage: response.data.discount_type === 'percentage' ? response.data.discount_value : null,
          fixed: response.data.discount_type === 'fixed' ? response.data.discount_value : null,
          finalPrice: response.data.final_price,
          discountAmount: response.data.discount_amount
        });
        setDiscountCode(code.trim().toUpperCase());
        toast.success(`¡Código aplicado! Ahorraste $${response.data.discount_amount.toFixed(2)}`);
      }
    } catch (error) {
      console.error('[Checkout] Error aplicando descuento desde URL:', error);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      // Permitir compra sin login - el usuario se registrará después del pago
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Pre-llenar el email si el usuario está logueado
      if (response.data?.email) {
        setGuestEmail(response.data.email);
      }
    } catch (error) {
      // Si el token es inválido, limpiar pero permitir continuar sin login
      localStorage.removeItem('session_token');
      setUser(null);
    }
    setLoading(false);
  };

  const loadExchangeRate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/income/exchange-rate`);
      if (response.data.usd_to_cop) {
        setExchangeRate(response.data.usd_to_cop);
      }
    } catch (error) {
      console.error('Error loading exchange rate:', error);
    }
  };

  const loadProductInfo = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    
    console.log('[Checkout] loadProductInfo - productType:', productType, 'productId:', productId);
    console.log('[Checkout] PLANS disponibles:', Object.keys(PLANS));
    console.log('[Checkout] PLANS[productId] existe:', !!PLANS[productId]);
    
    try {
      if (productType === 'plan' && PLANS[productId]) {
        // Planes de suscripción
        const planInfo = PLANS[productId];
        console.log('[Checkout] Usando plan desde backend:', planInfo);
        setProductInfo({ 
          id: productId, 
          ...planInfo,
          features: planInfo.features || [
            'Acceso inmediato al contenido',
            'Rutinas de ejercicio completas',
            'Plan de nutrición incluido',
            'Soporte por chat'
          ]
        });
      } else if (productType === 'challenge') {
        // Challenges
        const response = await axios.get(`${BACKEND_URL}/api/challenges/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductInfo({
          id: productId,
          name: response.data.title,
          description: response.data.short_description || response.data.description,
          price: response.data.price || 0,
          period: 'challenge',
          icon: Sparkles,
          color: 'pink',
          features: [
            `Duración: ${response.data.duration || 'Variable'}`,
            response.data.short_description || 'Acceso al challenge completo',
            'Seguimiento de progreso',
            'Comunidad de participantes'
          ]
        });
      } else if (productType === 'ebook' || productType === 'content' || productType === 'video') {
        // Ebooks y contenido individual - cargar desde el backend
        try {
          const response = await axios.get(`${BACKEND_URL}/api/content/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const content = response.data;
          setProductInfo({
            id: productId,
            name: content.title || 'Contenido Premium',
            description: content.description || content.short_description || '',
            price: content.price || 10,
            period: 'pago único',
            icon: Star,
            color: 'purple',
            features: content.features || [
              'Acceso inmediato al contenido',
              'Descarga disponible',
              'Acceso de por vida'
            ]
          });
        } catch (err) {
          // Si no se puede cargar, usar valores por defecto
          setProductInfo({
            id: productId,
            name: 'Contenido Premium',
            description: 'Acceso al contenido seleccionado',
            price: 10,
            period: 'pago único',
            icon: Star,
            color: 'purple',
            features: [
              'Acceso inmediato al contenido',
              'Descarga disponible',
              'Acceso de por vida'
            ]
          });
        }
      } else {
        setProductInfo({
          id: productId,
          name: 'Contenido Premium',
          description: 'Acceso al contenido seleccionado',
          price: 10,
          period: 'pago único',
          icon: Star,
          color: 'purple',
          features: ['Acceso inmediato', 'Contenido exclusivo']
        });
      }
    } catch (error) {
      console.error('Error loading product:', error);
      setProductInfo({ 
        id: productId, 
        name: 'Producto', 
        description: '', 
        price: 0, 
        period: '', 
        icon: Star, 
        color: 'pink',
        features: []
      });
    } finally {
      setLoading(false);
    }
  };

  const applyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Ingresa un código de descuento');
      return;
    }
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-discount`, {
        code: discountCode.trim().toUpperCase(),
        product_id: productInfo?.id || planId,
        original_price: productInfo?.price || 0
      });

      if (response.data.valid) {
        setAppliedDiscount({
          code: response.data.code,
          percentage: response.data.discount_type === 'percentage' ? response.data.discount_value : null,
          fixed: response.data.discount_type === 'fixed' ? response.data.discount_value : null,
          finalPrice: response.data.final_price,
          discountAmount: response.data.discount_amount
        });
        toast.success(`¡Código aplicado! Ahorraste $${response.data.discount_amount.toFixed(2)}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de descuento inválido');
      setAppliedDiscount(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    toast.info('Código de descuento removido');
  };

  const getFinalPrice = () => {
    // Si hay un periodo de precios seleccionado, usar su precio USD, aplicando
    // el discount_percent propio del periodo (igual que hace el backend en
    // server.py al cobrar), y luego el código promocional (monto fijo) si aplica.
    if (selectedPeriod && selectedPeriod.price_usd) {
      const periodDiscountPct = Number(selectedPeriod.discount_percent) || 0;
      const periodBase = Number(selectedPeriod.price_usd) || 0;
      const afterPeriodDiscount = periodDiscountPct > 0
        ? Math.round(periodBase * (1 - periodDiscountPct / 100) * 100) / 100
        : periodBase;
      if (appliedDiscount) {
        const promoAmount = Number(appliedDiscount.discountAmount) || 0;
        return Math.max(0, Math.round((afterPeriodDiscount - promoAmount) * 100) / 100);
      }
      return afterPeriodDiscount;
    }
    return appliedDiscount ? appliedDiscount.finalPrice : (productInfo?.price || 0);
  };
  const getFinalPriceCOP = () => {
    return Math.round(getFinalPrice() * exchangeRate);
  };

  const handleWompiCheckout = async () => {
    // Si el usuario NO está autenticado y aún NO ha creado cuenta, hacerlo primero
    if (!user && !accountCreated) {
      // Validaciones del formulario
      if (!guestName.trim() || guestName.trim().length < 2) {
        toast.error('Ingresa tu nombre completo');
        return;
      }
      if (!guestEmail || !guestEmail.includes('@')) {
        toast.error('Ingresa un correo electrónico válido');
        return;
      }
      if (!guestPassword || guestPassword.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (guestPassword !== guestPasswordConfirm) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
      // Crear cuenta
      setProcessing(true);
      try {
        const regRes = await axios.post(`${BACKEND_URL}/api/auth/register`, {
          email: guestEmail.trim().toLowerCase(),
          password: guestPassword,
          name: guestName.trim(),
          whatsapp: guestWhatsapp.trim() || null
        });
        if (regRes.data?.session_token) {
          localStorage.setItem('session_token', regRes.data.session_token);
          setUser(regRes.data.user);
          setAccountCreated(true);
          toast.success('¡Cuenta creada! Continuando al pago...');
        }
      } catch (err) {
        setProcessing(false);
        const detail = err.response?.data?.detail || '';
        if (detail.toLowerCase().includes('already') || detail.toLowerCase().includes('registered')) {
          toast.error('Este email ya está registrado. Inicia sesión primero.');
        } else {
          toast.error(detail || 'No se pudo crear la cuenta');
        }
        return;
      }
    }

    // Validar que haya email antes de continuar
    const checkoutEmail = guestEmail || user?.email || '';
    if (!checkoutEmail || !checkoutEmail.includes('@')) {
      toast.error('Por favor ingresa tu correo electrónico para continuar');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let response;
      const finalPrice = getFinalPrice();
      
      // Si el precio es 0 y NO hay usuario, pedir registro
      if (finalPrice <= 0 && !user) {
        toast.error('Para productos gratuitos debes iniciar sesión');
        navigate(`/login?redirect=/checkout?${productType}=${productId}`);
        return;
      }
      
      if (finalPrice <= 0 && user) {
        response = await axios.post(`${BACKEND_URL}/api/content/purchase-free`, {
          content_id: productId, content_type: productType, discount_code: appliedDiscount?.code || ''
        }, { headers });
        if (response.data.success) { toast.success('¡Compra gratuita completada!'); navigate('/dashboard'); return; }
      }
      
      if (productType === 'challenge') {
        // Challenges permiten pago sin registro
        response = await axios.post(`${BACKEND_URL}/api/challenges/enroll`, {
          challenge_id: productId, 
          name: user?.name || checkoutEmail.split('@')[0] || 'Comprador',
          email: checkoutEmail, 
          discount_code: appliedDiscount?.code || '', 
          origin_url: window.location.origin
        }, { headers });
        if (response.data.free_enrollment) { toast.success('¡Inscripción gratuita!'); if (response.data.redirect_url) window.location.href = response.data.redirect_url; return; }
      } else if (productType === 'plan') {
        // Planes también permiten pago sin registro (guest checkout)
        response = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, {
          package_id: productId,
          discount_code: appliedDiscount?.code || '',
          origin_url: window.location.origin,
          guest_email: checkoutEmail,
          // Si Premium VIP con periodo activo, incluir period_months
          ...(selectedPeriod && selectedPeriod.period_months ? { period_months: selectedPeriod.period_months } : {})
        }, { headers });
        if (response.data.free_purchase) { toast.success('¡Suscripción activada!'); navigate('/dashboard'); return; }
      } else {
        // Otros contenidos (ebooks, videos) también permiten guest checkout
        response = await axios.post(`${BACKEND_URL}/api/payments/create-content-checkout`, {
          content_id: productId, 
          content_type: productType, 
          discount_code: appliedDiscount?.code || '', 
          origin_url: window.location.origin,
          guest_email: checkoutEmail
        }, { headers });
        if (response.data.free_purchase) { toast.success('¡Contenido desbloqueado!'); navigate('/dashboard'); return; }
      }
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  const PlanIcon = productInfo?.icon || Star;
  const planColor = productInfo?.color || 'pink';

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-purple-500/10" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Volver</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Product Summary */}
          <div className="space-y-6">
            {/* Product Card */}
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-32 h-32 bg-${planColor}-500/20 rounded-full blur-2xl`} />
              
              <div className="relative">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br from-${planColor}-500 to-${planColor}-600 rounded-2xl flex items-center justify-center shadow-lg shadow-${planColor}-500/30`}>
                    <PlanIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-white mb-1">{productInfo?.name}</h1>
                    <p className="text-zinc-400 text-sm">{productInfo?.description}</p>
                  </div>
                </div>

                {/* Features - Solo mostrar si hay features */}
                {productInfo?.features && productInfo.features.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {productInfo.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-zinc-300">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-green-400" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Price breakdown */}
                <div className="bg-zinc-800/50 rounded-2xl p-5 space-y-3">
                  <div className="flex justify-between text-zinc-400">
                    <span>Subtotal</span>
                    <div className="text-right">
                      {selectedPeriod ? (
                        selectedPeriod.discount_percent > 0 ? (
                          <span className="text-zinc-500 line-through">${Number(selectedPeriod.price_usd || 0).toFixed(2)} USD</span>
                        ) : (
                          <span>${Number(selectedPeriod.price_usd || 0).toFixed(2)} USD</span>
                        )
                      ) : (
                        <>
                          {productInfo?.original_price && productInfo.original_price > productInfo.price && (
                            <span className="text-zinc-500 line-through mr-2">${productInfo.original_price} USD</span>
                          )}
                          <span>${productInfo?.price} USD</span>
                        </>
                      )}
                    </div>
                  </div>

                  {selectedPeriod && selectedPeriod.discount_percent > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Descuento periodo (-{selectedPeriod.discount_percent}%)
                      </span>
                      <span>-${(Number(selectedPeriod.price_usd || 0) * (Number(selectedPeriod.discount_percent) / 100)).toFixed(2)} USD</span>
                    </div>
                  )}

                  {appliedDiscount && (
                    <div className="flex justify-between text-green-400">
                      <span className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Descuento ({appliedDiscount.code})
                      </span>
                      <span>-${appliedDiscount.discountAmount?.toFixed(2)} USD</span>
                    </div>
                  )}
                  
                  <div className="border-t border-zinc-700 pt-3 flex justify-between items-end">
                    <div>
                      <span className="text-zinc-400 text-sm">Total a pagar</span>
                      <p className="text-3xl font-bold text-white">${getFinalPrice()} <span className="text-lg text-zinc-400">USD</span></p>
                    </div>
                    <span className="text-sm text-zinc-500 bg-zinc-800 px-3 py-1 rounded-full" data-testid="checkout-period-label">
                      {selectedPeriod?.label || productInfo?.period}
                    </span>
                  </div>
                  
                  <div className="text-center pt-2 border-t border-zinc-700">
                    <p className="text-zinc-400 text-sm">Equivalente en pesos colombianos</p>
                    <p className="text-xl font-bold text-pink-400">${getFinalPriceCOP().toLocaleString('es-CO')} COP</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Discount Code */}
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-pink-500" />
                Código de descuento
              </h3>
              
              {appliedDiscount ? (
                <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-green-400 font-bold">{appliedDiscount.code}</p>
                      <p className="text-green-300 text-sm">Ahorraste ${appliedDiscount.discountAmount?.toFixed(2)} USD</p>
                    </div>
                  </div>
                  <button onClick={removeDiscount} className="text-zinc-400 hover:text-red-400 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Ingresa tu código"
                    className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none uppercase"
                  />
                  <Button 
                    onClick={applyDiscount} 
                    disabled={validatingDiscount}
                    className="bg-pink-500 hover:bg-pink-600 text-white px-6"
                  >
                    {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                  </Button>
                </div>
              )}
            </div>

            {/* Formulario de crear cuenta si NO hay usuario autenticado */}
            {pricingPeriods.length > 0 && (
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6" data-testid="checkout-period-selector">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Elige tu plan de pago
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pricingPeriods.map((p) => {
                    const id = p.pricing_id || p._id;
                    const isSel = id === selectedPeriodId;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSelectedPeriodId(id)}
                        className={`text-left p-4 rounded-xl border transition-all ${isSel ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'}`}
                        data-testid={`checkout-period-option-${id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-white">{p.label || `${p.period_months} ${p.period_months === 1 ? 'mes' : 'meses'}`}</span>
                          {p.discount_percent > 0 && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                              -{p.discount_percent}%
                            </span>
                          )}
                        </div>
                        {/* Show the price actually charged. The -X% badge used to sit
                            next to the undiscounted price, so the customer saw a
                            discount and was billed the full amount. */}
                        {p.discount_percent > 0 ? (
                          <div className="flex items-baseline gap-2">
                            <span className="text-emerald-400 font-bold text-lg">
                              ${(Number(p.price_usd || 0) * (1 - Number(p.discount_percent) / 100)).toFixed(2)} USD
                            </span>
                            <span className="text-zinc-500 text-sm line-through">
                              ${Number(p.price_usd || 0).toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <div className="text-emerald-400 font-bold text-lg">${Number(p.price_usd || 0).toFixed(2)} USD</div>
                        )}
                        {p.price_cop > 0 && (
                          <div className="text-zinc-400 text-sm">${Number(p.price_cop).toLocaleString('es-CO')} COP</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Formulario de crear cuenta si NO hay usuario autenticado */}
            {!user ? (
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-pink-500/30 rounded-2xl p-6" data-testid="checkout-register-form">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-pink-500" />
                  Crea tu cuenta para continuar
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  Tu cuenta quedará activa al completar el pago. Con estos datos podrás iniciar sesión y acceder a lo que compres.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">Nombre completo <span className="text-pink-500">*</span></label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Ej: María Fernanda García"
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                      data-testid="checkout-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">Correo electrónico <span className="text-pink-500">*</span></label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                      data-testid="checkout-email-input"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-zinc-300 text-xs mb-1">Contraseña <span className="text-pink-500">*</span></label>
                      <input
                        type="password"
                        value={guestPassword}
                        onChange={(e) => setGuestPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                        data-testid="checkout-password-input"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 text-xs mb-1">Confirmar contraseña <span className="text-pink-500">*</span></label>
                      <input
                        type="password"
                        value={guestPasswordConfirm}
                        onChange={(e) => setGuestPasswordConfirm(e.target.value)}
                        placeholder="Repite la contraseña"
                        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                        data-testid="checkout-password-confirm-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">WhatsApp (opcional)</label>
                    <input
                      type="tel"
                      value={guestWhatsapp}
                      onChange={(e) => setGuestWhatsapp(e.target.value)}
                      placeholder="Ej: +57 300 1234567"
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
                      data-testid="checkout-whatsapp-input"
                    />
                  </div>
                  <p className="text-zinc-500 text-xs pt-2">
                    ¿Ya tienes cuenta?{' '}
                    <button
                      type="button"
                      onClick={() => navigate(`/login?redirect=/checkout?${productType}=${productId}`)}
                      className="text-pink-400 hover:text-pink-300 underline"
                      data-testid="checkout-login-link"
                    >
                      Inicia sesión aquí
                    </button>
                  </p>
                </div>
              </div>
            ) : (
              /* Usuario autenticado: solo mostrar el email */
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-pink-500" />
                  Comprando con la cuenta
                </h3>
                <p className="text-zinc-300 text-sm" data-testid="checkout-user-email">
                  {user.email}
                </p>
              </div>
            )}
          </div>

          {/* Right: Payment Methods */}
          <div className="space-y-6">
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-pink-500" />
                Método de pago
              </h2>

              {/* Precio en ambas monedas */}
              <div className="bg-zinc-800/50 rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-zinc-400">Tu compra:</span>
                  <span className="text-white font-bold">{productInfo?.name}</span>
                </div>
                
                {/* Precio en USD */}
                <div className="flex items-center justify-between py-3 border-b border-zinc-700">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="text-zinc-300">Precio USD</span>
                  </div>
                  <span className="text-green-400 font-bold text-xl">${getFinalPrice()} USD</span>
                </div>
                
                {/* Precio en COP */}
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-pink-400 font-bold">$</span>
                    <span className="text-zinc-300">Precio COP</span>
                  </div>
                  <span className="text-pink-400 font-bold text-xl">${getFinalPriceCOP().toLocaleString('es-CO')} COP</span>
                </div>
                
                <p className="text-zinc-500 text-xs mt-3 text-center">
                  Tasa de cambio: 1 USD = ${exchangeRate.toLocaleString('es-CO')} COP
                </p>
              </div>

              {/* Wompi - único método de pago */}
              <div className="w-full p-5 rounded-2xl border-2 border-pink-500 bg-pink-500/10 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-pink-500">
                    <span className="text-white font-bold text-lg">W</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">Wompi</p>
                    <p className="text-zinc-400 text-sm">Nequi • PSE • Bancolombia • Tarjetas</p>
                  </div>
                  <div className="w-6 h-6 rounded-full border-2 border-pink-500 bg-pink-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-pink-500/30">
                  <p className="text-pink-400 text-sm font-medium">💳 Acepta tarjetas de cualquier país</p>
                  <p className="text-zinc-400 text-xs mt-1">Visa, Mastercard, Nequi, PSE, Bancolombia, Daviplata</p>
                </div>
              </div>

              {/* Payment Button */}
              <Button
                onClick={handleWompiCheckout}
                disabled={processing || !productInfo}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-pink-500/30 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Procesando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Lock className="w-5 h-5" />
                    Pagar ${getFinalPriceCOP().toLocaleString('es-CO')} COP
                  </span>
                )}
              </Button>

              {/* Security badges */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="flex items-center justify-center gap-6 text-zinc-500 text-xs">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>Pago seguro</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <span>SSL encriptado</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Help text */}
            <div className="text-center text-zinc-500 text-sm">
              <p>¿Necesitas ayuda? Escríbenos a <span className="text-pink-400">soporte@bootyandlegs.com</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
